import { networkManager } from './NetworkManager';

interface ApiResponse<T> {
  status: boolean;
  data: T;
}

interface PaginatedResponse<T> {
  status: boolean;
  data: T[];
  pagination: { lastKey?: string; hasMore: boolean };
}

// --- Auth ---

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: 'Bearer';
}

export interface UserProfile {
  sub: string;
  yumId: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  roles: string[];
  capabilities: string[];
  jobRole?: string;
  company?: string;
  brand?: string;
  bmu?: string;
  businessUnit?: string;
  costCentre?: string;
  department?: string;
  countryCode?: string;
  workCountry?: string;
}

export interface RegistrationPrefill {
  yumId: string;
  email: string;
  firstName: string;
  lastName: string;
  jobRole?: string;
  company?: string;
  brand?: string;
  bmu?: string;
  businessUnit?: string;
  costCentre?: string;
  department?: string;
  countryCode?: string;
  workCountry?: string;
}

export type OtpVerifyResult =
  | ({
      isNewUser: false;
      profile: UserProfile;
    } & AuthTokens)
  | {
      isNewUser: true;
      registrationToken: string;
      profile: RegistrationPrefill;
    };

export async function sendOtp(yumId: string): Promise<void> {
  await networkManager.post<ApiResponse<null>>('/api/auth/otp/send', { yumId });
}

export async function verifyOtp(yumId: string, otp: string): Promise<OtpVerifyResult> {
  const response = await networkManager.post<ApiResponse<OtpVerifyResult>>(
    '/api/auth/otp/verify',
    { yumId, otp },
  );
  return response.data;
}

export interface RegisterInput {
  registrationToken: string;
  firstName: string;
  lastName: string;
  displayName?: string;
}

export async function registerUser(
  input: RegisterInput,
): Promise<AuthTokens & { profile: UserProfile }> {
  const response = await networkManager.post<ApiResponse<AuthTokens & { profile: UserProfile }>>(
    '/api/auth/register',
    input,
  );
  return response.data;
}

export async function refreshAccessToken(
  refreshToken: string,
): Promise<Omit<AuthTokens, 'refreshToken'>> {
  const response = await networkManager.post<ApiResponse<Omit<AuthTokens, 'refreshToken'>>>(
    '/api/auth/refresh',
    { refreshToken },
  );
  return response.data;
}

export async function getCurrentUser(): Promise<UserProfile> {
  const response = await networkManager.get<ApiResponse<UserProfile>>('/api/auth/me');
  return response.data;
}

// --- Users (Admin) ---

export interface UserListItem {
  sub: string;
  yumId: string;
  email: string;
  name: string;
  roles: string[];
}

/**
 * Batch-resolve yumIds to minimal public profiles. Used by UIs that store
 * references by yumId and need to render names (e.g. event judge lists).
 * Any authenticated user may call this.
 */
export async function lookupUsers(yumIds: string[]): Promise<UserListItem[]> {
  if (yumIds.length === 0) return [];
  const response = await networkManager.post<ApiResponse<UserListItem[]>>(
    '/api/users/lookup',
    { yumIds },
  );
  return response.data;
}

export async function listUsers(options?: {
  search?: string;
  limit?: number;
  lastKey?: string;
}): Promise<{ users: UserListItem[]; lastKey?: string; hasMore: boolean }> {
  const params = new URLSearchParams();
  if (options?.search) params.set('search', options.search);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.lastKey) params.set('lastKey', options.lastKey);
  const qs = params.toString();
  const response = await networkManager.get<PaginatedResponse<UserListItem>>(
    `/api/users${qs ? `?${qs}` : ''}`,
  );
  return {
    users: response.data,
    lastKey: response.pagination.lastKey,
    hasMore: response.pagination.hasMore,
  };
}

export async function updateUserRoles(userId: string, roles: string[]): Promise<UserProfile> {
  const response = await networkManager.put<ApiResponse<UserProfile>>(
    `/api/users/${userId}/roles`,
    { roles },
  );
  return response.data;
}

export async function bulkAssignRoles(
  action: 'add' | 'remove',
  role: string,
  userIds: string[],
): Promise<{ total: number; successful: number; failed: number; results: Array<{ userId: string; success: boolean; roles?: string[]; error?: string }> }> {
  const response = await networkManager.post<ApiResponse<{ total: number; successful: number; failed: number; results: Array<{ userId: string; success: boolean; roles?: string[]; error?: string }> }>>(
    '/api/bulk/roles',
    { action, role, userIds },
  );
  return response.data;
}

// --- Health ---

export async function healthCheck(): Promise<{
  service: string;
  version: string;
  uptime: number;
}> {
  const response = await networkManager.get<
    ApiResponse<{ service: string; version: string; uptime: number }>
  >('/api/health');
  return response.data;
}

// --- Events ---

export interface EventTheme {
  id: string;
  name: string;
  description: string;
}

export interface ScoringCriterion {
  name: string;
  weight: number;
  maxScore: number;
  description?: string;
}

export interface EventSettings {
  minTeamSize: number;
  maxTeamSize: number;
  timerDurationSeconds: number;
  allowSelfJoin: boolean;
  enableMentors: boolean;
}

export interface EventItem {
  eventId: string;
  name: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  submissionDeadline: string;
  themes: EventTheme[];
  scoringCriteria: ScoringCriterion[];
  settings: EventSettings;
  isTest?: boolean;
  coverImageUrl?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  leaderboardVisibility?: string;
}

export async function listEvents(options?: {
  status?: string;
  limit?: number;
  lastKey?: string;
  includeTest?: boolean;
}): Promise<{ events: EventItem[]; lastKey?: string; hasMore: boolean }> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.lastKey) params.set('lastKey', options.lastKey);
  if (options?.includeTest) params.set('includeTest', 'true');
  const qs = params.toString();
  const response = await networkManager.get<PaginatedResponse<EventItem>>(
    `/api/events${qs ? `?${qs}` : ''}`,
  );
  return {
    events: response.data,
    lastKey: response.pagination?.lastKey,
    hasMore: response.pagination?.hasMore ?? false,
  };
}

export async function getEvent(eventId: string): Promise<EventItem> {
  const response = await networkManager.get<ApiResponse<EventItem>>(`/api/events/${eventId}`);
  return response.data;
}

export async function createEvent(data: {
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  submissionDeadline: string;
  themes: Array<{ name: string; description: string }>;
  scoringCriteria: Array<{ name: string; weight: number; maxScore: number }>;
  settings?: Partial<EventSettings>;
  isTest?: boolean;
}): Promise<EventItem> {
  const response = await networkManager.post<ApiResponse<EventItem>>('/api/events', data);
  return response.data;
}

export async function updateEvent(
  eventId: string,
  data: Partial<{
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    submissionDeadline: string;
    themes: Array<{ name: string; description: string }>;
    scoringCriteria: Array<{ name: string; weight: number; maxScore: number }>;
    settings: Partial<EventSettings>;
    judges: string[];
    isTest: boolean;
    coverImageUrl: string;
  }>,
): Promise<EventItem> {
  const response = await networkManager.put<ApiResponse<EventItem>>(
    `/api/events/${eventId}`,
    data,
  );
  return response.data;
}

export async function transitionEvent(
  eventId: string,
  action: string,
): Promise<{ status: string }> {
  const response = await networkManager.post<ApiResponse<{ status: string }>>(
    `/api/events/${eventId}/transition`,
    { action },
  );
  return response.data;
}

// --- Teams ---

export interface TeamMember {
  sub: string;
  name: string;
  email: string;
  role: string;
}

export interface TeamItem {
  teamId: string;
  name: string;
  themeId: string | null;
  inviteCode: string;
  members: TeamMember[];
  teamPhotoUrl?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  progress?: number;
}

export async function listTeams(
  eventId: string,
  options?: { limit?: number; lastKey?: string },
): Promise<{ teams: TeamItem[]; lastKey?: string; hasMore: boolean }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.lastKey) params.set('lastKey', options.lastKey);
  const qs = params.toString();
  const response = await networkManager.get<PaginatedResponse<TeamItem>>(
    `/api/events/${eventId}/teams${qs ? `?${qs}` : ''}`,
  );
  return {
    teams: response.data,
    lastKey: response.pagination?.lastKey,
    hasMore: response.pagination?.hasMore ?? false,
  };
}

export async function getTeam(eventId: string, teamId: string): Promise<TeamItem> {
  const response = await networkManager.get<ApiResponse<TeamItem>>(
    `/api/events/${eventId}/teams/${teamId}`,
  );
  return response.data;
}

export async function createTeam(
  eventId: string,
  data: { name: string; themeId: string },
): Promise<TeamItem> {
  const response = await networkManager.post<ApiResponse<TeamItem>>(
    `/api/events/${eventId}/teams`,
    data,
  );
  return response.data;
}

export async function joinTeamByCode(
  eventId: string,
  inviteCode: string,
): Promise<TeamItem> {
  const response = await networkManager.post<ApiResponse<TeamItem>>(
    `/api/events/${eventId}/teams/join`,
    { inviteCode },
  );
  return response.data;
}

export async function joinTeam(
  eventId: string,
  teamId: string,
  inviteCode?: string,
): Promise<TeamItem> {
  const response = await networkManager.post<ApiResponse<TeamItem>>(
    `/api/events/${eventId}/teams/${teamId}/join`,
    inviteCode ? { inviteCode } : {},
  );
  return response.data;
}

export async function leaveTeam(eventId: string, teamId: string): Promise<void> {
  await networkManager.post(`/api/events/${eventId}/teams/${teamId}/leave`);
}

export async function getTeamPhotoUploadUrl(
  eventId: string,
  teamId: string,
  fileName: string,
  contentType: string,
): Promise<{ uploadUrl: string; photoUrl: string }> {
  const response = await networkManager.post<ApiResponse<{ uploadUrl: string; photoUrl: string }>>(
    `/api/events/${eventId}/teams/${teamId}/team-photo/upload`,
    { fileName, contentType },
  );
  return response.data;
}

export async function confirmTeamPhoto(
  eventId: string,
  teamId: string,
  photoUrl: string,
): Promise<{ teamId: string; teamPhotoUrl: string }> {
  const response = await networkManager.put<ApiResponse<{ teamId: string; teamPhotoUrl: string }>>(
    `/api/events/${eventId}/teams/${teamId}/team-photo`,
    { photoUrl },
  );
  return response.data;
}

// --- Submissions ---

export interface Submission {
  submissionId: string;
  teamId: string;
  eventId: string;
  title: string;
  problem: string;
  solution: string;
  impact: string;
  technicalApproach: string;
  demoUrl: string;
  repoUrl: string;
  fileUrls: string[];
  status: 'draft' | 'submitted' | 'presented';
  progress?: number;
  createdAt: string;
  updatedAt: string;
}

export async function getSubmission(
  eventId: string,
  teamId: string,
): Promise<Submission> {
  const response = await networkManager.get<ApiResponse<Submission>>(
    `/api/events/${eventId}/teams/${teamId}/submissions`,
  );
  return response.data;
}

export async function createSubmission(
  eventId: string,
  teamId: string,
): Promise<Submission> {
  const response = await networkManager.post<ApiResponse<Submission>>(
    `/api/events/${eventId}/teams/${teamId}/submissions`,
  );
  return response.data;
}

export async function updateSubmission(
  eventId: string,
  teamId: string,
  submissionId: string,
  updates: Partial<{
    title: string;
    problem: string;
    solution: string;
    impact: string;
    technicalApproach: string;
    demoUrl: string;
    repoUrl: string;
  }>,
): Promise<{ submissionId: string; updatedAt: string }> {
  const response = await networkManager.put<
    ApiResponse<{ submissionId: string; updatedAt: string }>
  >(`/api/events/${eventId}/teams/${teamId}/submissions/${submissionId}`, updates);
  return response.data;
}

export async function submitSubmission(
  eventId: string,
  teamId: string,
  submissionId: string,
): Promise<{ submissionId: string; status: string; submittedAt: string }> {
  const response = await networkManager.post<
    ApiResponse<{ submissionId: string; status: string; submittedAt: string }>
  >(`/api/events/${eventId}/teams/${teamId}/submissions/${submissionId}/submit`);
  return response.data;
}

export async function unsubmitSubmission(
  eventId: string,
  teamId: string,
  submissionId: string,
): Promise<{ submissionId: string; status: string }> {
  const response = await networkManager.post<
    ApiResponse<{ submissionId: string; status: string }>
  >(`/api/events/${eventId}/teams/${teamId}/submissions/${submissionId}/unsubmit`);
  return response.data;
}

// --- Feedback ---

export interface FeedbackItem {
  feedbackId: string;
  mentorName: string;
  content: string;
  section: string;
  type: string;
  createdAt: string;
}

export async function createFeedback(
  eventId: string,
  teamId: string,
  data: { section: string; type: string; content: string },
): Promise<FeedbackItem> {
  const response = await networkManager.post<ApiResponse<FeedbackItem>>(
    `/api/events/${eventId}/teams/${teamId}/feedback`,
    data,
  );
  return response.data;
}

export async function listFeedback(
  eventId: string,
  teamId: string,
): Promise<FeedbackItem[]> {
  const response = await networkManager.get<ApiResponse<FeedbackItem[]>>(
    `/api/events/${eventId}/teams/${teamId}/feedback`,
  );
  return response.data;
}

// --- Scores ---

export interface ScoreRecord {
  scoreId: string;
  eventId: string;
  teamId: string;
  judgeId: string;
  criteria: Array<{ name: string; score: number; comment?: string }>;
  weightedTotal: number;
  createdAt: string;
}

export async function submitScores(
  eventId: string,
  data: {
    teamId: string;
    criteria: Array<{ name: string; score: number; comment?: string }>;
  },
): Promise<ScoreRecord> {
  const response = await networkManager.post<ApiResponse<ScoreRecord>>(
    `/api/events/${eventId}/scores`,
    data,
  );
  return response.data;
}

export async function getTeamScores(
  eventId: string,
  teamId: string,
): Promise<ScoreRecord[]> {
  const response = await networkManager.get<ApiResponse<ScoreRecord[]>>(
    `/api/events/${eventId}/scores/teams/${teamId}`,
  );
  return response.data;
}

export async function getAllScores(
  eventId: string,
): Promise<ScoreRecord[]> {
  const response = await networkManager.get<ApiResponse<ScoreRecord[]>>(
    `/api/events/${eventId}/scores`,
  );
  return response.data;
}

// --- Judging Summary ---

export interface TeamJudgingSummary {
  teamId: string;
  teamName: string;
  averageScore: number;
  strengths: string;
  weaknesses: string;
  keyTakeaways: string;
}

export interface JudgingSummaryResult {
  eventSummary: string;
  teamSummaries: TeamJudgingSummary[];
  generatedAt: string;
}

export async function generateJudgingSummary(
  eventId: string,
): Promise<JudgingSummaryResult> {
  const response = await networkManager.post<ApiResponse<JudgingSummaryResult>>(
    `/api/events/${eventId}/scores/judging-summary`,
  );
  return response.data;
}

// --- Leaderboard ---

export interface LeaderboardEntry {
  rank: number;
  teamId: string;
  teamName: string;
  theme: string;
  averageScore: number;
  judgeCount: number;
  badges: string[];
  productName?: string;
  productSummary?: string;
  feedbackSummary?: {
    text: string;
    generatedAt: string;
    basedOnScoreCount: number;
  };
  teamPhotoUrl?: string;
}

export async function getLeaderboard(
  eventId: string,
): Promise<LeaderboardEntry[]> {
  const response = await networkManager.get<ApiResponse<LeaderboardEntry[]>>(
    `/api/events/${eventId}/scores/leaderboard`,
  );
  return response.data;
}

export async function updateLeaderboardVisibility(
  eventId: string,
  visibility: 'hidden' | 'judges_only' | 'public',
): Promise<{ leaderboardVisibility: string }> {
  const response = await networkManager.put<
    ApiResponse<{ leaderboardVisibility: string }>
  >(`/api/events/${eventId}/scores/leaderboard`, { visibility });
  return response.data;
}

// --- Badges ---

export interface Badge {
  badgeId: string;
  teamId: string;
  badge: string;
  awardedBy: string;
  awardedAt: string;
}

export async function awardBadge(
  eventId: string,
  teamId: string,
  badge: string,
): Promise<Badge> {
  const response = await networkManager.post<ApiResponse<Badge>>(
    `/api/events/${eventId}/scores/badges`,
    { teamId, badge },
  );
  return response.data;
}

export async function listBadges(eventId: string): Promise<Badge[]> {
  const response = await networkManager.get<ApiResponse<Badge[]>>(
    `/api/events/${eventId}/scores/badges`,
  );
  return response.data;
}

// --- Live Session ---

export interface LiveTeamEntry {
  teamId: string;
  teamName: string;
  themeName?: string;
  status: 'waiting' | 'presenting' | 'completed' | 'skipped';
}

export interface LiveSession {
  sessionId: string;
  eventId: string;
  status: 'waiting' | 'active' | 'paused' | 'ended';
  currentTeamId: string | null;
  currentTeamIndex: number;
  totalTeams: number;
  teamOrder: LiveTeamEntry[];
  timerState: 'stopped' | 'running' | 'paused' | 'expired';
  timerDurationSeconds: number;
  timerRemainingSeconds: number;
  timerStartedAt: string | null;
  timerElapsedBeforePause: number;
  createdAt: string;
}

export async function createLiveSession(
  eventId: string,
  data: { teamOrder: string[]; timerDurationSeconds: number },
): Promise<LiveSession> {
  const response = await networkManager.post<ApiResponse<LiveSession>>(
    `/api/events/${eventId}/live`,
    data,
  );
  return response.data;
}

export async function getLiveSession(eventId: string): Promise<LiveSession> {
  const response = await networkManager.get<ApiResponse<LiveSession>>(
    `/api/events/${eventId}/live`,
  );
  return response.data;
}

export async function controlLiveSession(
  eventId: string,
  action: string,
): Promise<LiveSession> {
  const response = await networkManager.put<ApiResponse<LiveSession>>(
    `/api/events/${eventId}/live`,
    { action },
  );
  return response.data;
}

export async function endLiveSession(
  eventId: string,
): Promise<{ teamsCompleted: number; teamsSkipped: number; totalTeams: number }> {
  const response = await networkManager.delete<
    ApiResponse<{ teamsCompleted: number; teamsSkipped: number; totalTeams: number }>
  >(`/api/events/${eventId}/live`);
  return response.data;
}

// --- AI ---

export interface RefineResult {
  refined: string;
  suggestions: string[];
  remainingQuota?: number;
}

export async function refineIdea(data: {
  eventId?: string;
  teamId?: string;
  section: string;
  content: string;
}): Promise<RefineResult> {
  const response = await networkManager.post<ApiResponse<RefineResult>>(
    '/api/ai/refine',
    data,
  );
  return response.data;
}

export interface GeneratePrdResult {
  prd: string;
  remainingQuota?: number;
}

export async function generatePrd(data: {
  eventId?: string;
  teamId?: string;
  title: string;
  problem: string;
  solution?: string;
}): Promise<GeneratePrdResult> {
  const response = await networkManager.post<ApiResponse<GeneratePrdResult>>(
    '/api/ai/generate-prd',
    data,
  );
  return response.data;
}

export interface SummarizeResult {
  summary: string;
  strengths: string[];
  risks: string[];
  questions: string[];
  cached?: boolean;
  generatedAt?: string;
}

export async function summarizeSubmission(data: {
  eventId: string;
  teamId: string;
  submissionId?: string;
  forceRegenerate?: boolean;
}): Promise<SummarizeResult> {
  const response = await networkManager.post<ApiResponse<SummarizeResult>>(
    '/api/ai/summarize',
    data,
  );
  return response.data;
}

// --- Event Feedback ---

export interface FeedbackFormQuestion {
  id: string;
  type: 'text' | 'rating';
  label: string;
  maxRating?: number;
}

export interface FeedbackFormConfig {
  enabled: boolean;
  introHtml: string;
  questions: FeedbackFormQuestion[];
}

export async function getEventFeedbackForm(eventId: string): Promise<FeedbackFormConfig> {
  const response = await networkManager.get<ApiResponse<FeedbackFormConfig>>(
    `/api/events/${eventId}/event-feedback/form`,
  );
  return response.data;
}

export async function submitEventFeedback(
  eventId: string,
  answers: Array<{ questionId: string; value: string | number }>,
): Promise<{ feedbackId: string; submittedAt: string }> {
  const response = await networkManager.post<ApiResponse<{ feedbackId: string; submittedAt: string }>>(
    `/api/events/${eventId}/event-feedback`,
    { answers },
  );
  return response.data;
}

export async function getMyEventFeedback(
  eventId: string,
): Promise<{ submitted: boolean; submittedAt?: string; answers?: Array<{ questionId: string; value: string | number }> }> {
  const response = await networkManager.get<ApiResponse<{ submitted: boolean; submittedAt?: string; answers?: Array<{ questionId: string; value: string | number }> }>>(
    `/api/events/${eventId}/event-feedback/mine`,
  );
  return response.data;
}

export async function listEventFeedback(
  eventId: string,
): Promise<Array<{ feedbackId: string; userId: string; userName: string; answers: Array<{ questionId: string; value: string | number }>; submittedAt: string }>> {
  const response = await networkManager.get<ApiResponse<Array<{ feedbackId: string; userId: string; userName: string; answers: Array<{ questionId: string; value: string | number }>; submittedAt: string }>>>(
    `/api/events/${eventId}/event-feedback`,
  );
  return response.data;
}

// --- Bulk Export ---

export async function exportData(
  type: 'submissions' | 'scores' | 'leaderboard',
  eventId: string,
): Promise<unknown> {
  const response = await networkManager.post<ApiResponse<unknown>>(
    `/api/bulk/export/${type}`,
    { eventId },
  );
  return response.data;
}

// --- Clone Event ---

export async function cloneEvent(eventId: string): Promise<EventItem> {
  const response = await networkManager.post<ApiResponse<EventItem>>(
    `/api/events/${eventId}/clone`,
  );
  return response.data;
}

// --- Bulk Invite ---

export interface BulkInviteResult {
  total: number;
  successful: number;
  failed: number;
  alreadyInvited: number;
  errors: Array<{ row: number; email: string; reason: string }>;
}

export async function bulkInvite(
  eventId: string,
  invites: Array<{ email: string; name?: string; role?: string }>,
): Promise<BulkInviteResult> {
  const response = await networkManager.post<ApiResponse<BulkInviteResult>>(
    '/api/bulk/invite',
    { eventId, invites },
  );
  return response.data;
}

// --- Notifications ---

export interface NotificationItem {
  notificationId: string;
  message: string;
  audience: string;
  recipientTeams: number;
  recipientUsers: number;
  senderName: string;
  sentAt: string;
}

export async function sendNotification(
  eventId: string,
  message: string,
  audience: 'all' | 'unsubmitted' | 'submitted',
): Promise<NotificationItem> {
  const response = await networkManager.post<ApiResponse<NotificationItem>>(
    '/api/bulk/notifications',
    { eventId, message, audience },
  );
  return response.data;
}

export async function listNotifications(eventId: string): Promise<NotificationItem[]> {
  const response = await networkManager.get<ApiResponse<NotificationItem[]>>(
    `/api/bulk/notifications/${eventId}`,
  );
  return response.data;
}
